import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { FormControlLabel, Grid } from '@mui/material';
import { Switch } from '@components/Switch';
import { Typography } from '@components/Typography';
import HoursIntervals from '@features/HoursIntervals';
import { ProfileName } from '@constants/integrations';
import { DEFAULT_HOURS_INTERVAL, MAX_FACEBOOK_DAY_INTERVALS } from '@constants/companies';
import { useTranslation } from '@hooks';

const RowWrapper = styled(Grid)({
    alignItems: 'flex-start',
    minHeight: '40px',
    padding: '4px',
    '& .MuiFormControl-root': {
        paddingBottom: 0,
    },
});

const initialDaysState = [1, 2, 3, 4, 5, 6, 0].map(day => ({
    day,
    enabled: false,
    hours: [{ ...DEFAULT_HOURS_INTERVAL }],
}));

const BusinessHoursSelection = ({ data, onChangeHours, withAdditionalTime = false, type = null, disabled = false }) => {
    const { t } = useTranslation();

    const [workingDays, setWorkingDays] = useState(initialDaysState);

    const DAY_NAMES = {
        0: t('common.sunday'),
        1: t('common.monday'),
        2: t('common.tuesday'),
        3: t('common.wednesday'),
        4: t('common.thursday'),
        5: t('common.friday'),
        6: t('common.saturday'),
    };

    useEffect(() => {
        if (data) {
            const updatedDays = initialDaysState.map(day => {
                const matchingDay = data.find(item => item.day === day.day);
                return matchingDay ? { ...day, ...matchingDay, enabled: true } : { ...day, enabled: false };
            });
            setWorkingDays(updatedDays);
        }
    }, [data]);

    const update = newWorkingDays => {
        setWorkingDays(newWorkingDays);
        onChangeHours(newWorkingDays.filter(day => day.enabled));
    };

    const toggleDay = (dayIndex, enabled) =>
        update(workingDays.map((day, index) => (index === dayIndex ? { ...day, enabled } : day)));

    const changeHours = (dayIndex, hours) =>
        update(workingDays.map((day, index) => (index === dayIndex ? { ...day, hours } : day)));

    const canAddInterval = day =>
        withAdditionalTime &&
        !(type === ProfileName.FacebookOfficial && day.hours.length >= MAX_FACEBOOK_DAY_INTERVALS);

    return (
        <Grid container rowSpacing={0.5}>
            {workingDays.map((currentDay, dayIndex) => (
                <RowWrapper
                    key={currentDay.day}
                    container
                    size={12}
                    rowSpacing={0}
                    columnSpacing={1}
                    columns={{ xs: 9, md: 16 }}
                >
                    <Grid size={3} sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                        <div>
                            <Typography variant="body2" color="textPrimary">
                                {DAY_NAMES[currentDay.day]}
                            </Typography>
                        </div>
                    </Grid>
                    <Grid size={{ md: 4 }}>
                        <FormControlLabel
                            sx={{ height: '40px' }}
                            control={
                                <Switch
                                    checked={currentDay.enabled}
                                    handleChange={e => toggleDay(dayIndex, e.target.checked)}
                                    disabled={disabled}
                                />
                            }
                            label={
                                <Typography variant="body2" color="textPrimary">
                                    {currentDay.enabled ? t('common.open') : t('common.closed')}
                                </Typography>
                            }
                        />
                    </Grid>
                    <Grid>
                        {currentDay.enabled && (
                            <HoursIntervals
                                hours={currentDay.hours}
                                onChange={hours => changeHours(dayIndex, hours)}
                                canAdd={canAddInterval(currentDay)}
                                disabled={disabled}
                            />
                        )}
                    </Grid>
                </RowWrapper>
            ))}
        </Grid>
    );
};

export default BusinessHoursSelection;
